// @flow
import {
    ClickableArea,
    CloseBorder,
    CloseGroup,
    CloseText,
    Container,
    CopyButton,
    CopyButtonContainer,
    CrossPath,
    DayLabel,
    EventsContainer,
    Line,
    Mask,
    TextContainer
} from './style';
import { injectIntl, intlShape } from 'react-intl';

import AddEventButton from './add-event-button';
import { EASING } from '../constants';
import Event from '../event';
import ImmutablePropTypes from 'react-immutable-proptypes';
import messages from '~/core/domains/schedule/intl';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import * as React from 'react';
import { scheduleModesEnum } from '~/core/constants/schedule';
import { VelocityTransitionGroup } from 'velocity-react';

const MINUTES_IN_DAY = 1440;

Day.propTypes = {
    componentId: PropTypes.string.isRequired,
    copyDay: ImmutablePropTypes.map,
    currentEvent: PropTypes.object.isRequired,
    dayIndex: PropTypes.number.isRequired,
    events: ImmutablePropTypes.list.isRequired,
    height: PropTypes.number.isRequired,
    intl: intlShape,
    isDayDragging: PropTypes.bool.isRequired,
    maxEvents: PropTypes.number.isRequired,
    scheduleMode: PropTypes.string.isRequired,
    selectDay: PropTypes.func.isRequired,
    selectedDay: PropTypes.number,
    sideMarginWidth: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired
};

function Day({
    componentId,
    copyDay,
    currentEvent,
    dayIndex,
    events,
    height,
    intl: { formatMessage },
    isDayDragging,
    maxEvents,
    scheduleMode,
    selectDay,
    selectedDay,
    sideMarginWidth,
    top,
    width
}) {
    const isCopyMode = scheduleMode === scheduleModesEnum.COPY_DAY;
    const isAddMode = scheduleMode === scheduleModesEnum.ADD_EVENT;
    const isDeletedMode = scheduleMode === scheduleModesEnum.DELETE_EVENT;
    const isSelectedDay = selectedDay === dayIndex;
    const usableWidth = width - (isCopyMode ? 2.7 : 2) * sideMarginWidth;
    const getXPos = start => (isCopyMode ? 1.7 : 1) * sideMarginWidth + start * usableWidth / MINUTES_IN_DAY;
    const eventHeight = isSelectedDay ? height / events.size : height;
    const addEventButtons = [];
    const eventsList = events.map((event, i) => {
        const start = i === 0 ? 0 : event.get('start');
        const end = events.getIn([i + 1, 'start'], MINUTES_IN_DAY);
        const xPos = getXPos(start);
        const eventWidth = getXPos(end) - xPos;
        const yPos = top + (isSelectedDay ? eventHeight * i : 0);
        const isCurrentEvent =
            currentEvent.get('dayIndex') === event.get('dayIndex') &&
            currentEvent.get('eventIndex') === event.get('eventIndex');
        const isLastEvent = i === events.size - 1;
        if (isAddMode && isSelectedDay && events.size <= maxEvents && !isLastEvent && end - start > 15) {
            addEventButtons.push(
                <AddEventButton
                    componentId={componentId}
                    event={event}
                    eventHeight={eventHeight}
                    key={`add-event-${event.get('guid')}`}
                    left={xPos + eventWidth}
                    maxEvents={maxEvents}
                    scheduleMode={scheduleMode}
                    top={yPos + eventHeight}
                />
            );
        }
        return (
            <Event
                canBeDeleted={isSelectedDay && isDeletedMode && i !== 0 && events.size > 2}
                componentId={componentId}
                containerWidth={width}
                event={event}
                height={eventHeight}
                isCurrentEvent={isCurrentEvent}
                isDayDragging={isDayDragging}
                isDayShrunk={selectedDay !== null && !isSelectedDay}
                isFirstEvent={i === 0}
                isLastEvent={isLastEvent}
                isSelectedDay={isSelectedDay}
                key={`event-${event.get('guid')}`}
                left={xPos}
                sideMarginWidth={sideMarginWidth}
                top={yPos}
                width={eventWidth}
            />
        );
    });
    const isSelectedToBeCopied = !!(copyDay && copyDay.get('dayToCopy') === dayIndex);
    const isCopiedTo = !!(copyDay && copyDay.get('copyToList').includes(dayIndex));

    const crossSize = 12;
    const closeTextWidth = 60;
    const closeButtonWidth = closeTextWidth + 3 * crossSize;
    const closeButtonHeight = 25;
    const closeButtonLeft = width - (sideMarginWidth + closeButtonWidth) - 1;
    return (
        <Container data-qa={`schedule-weekday-${dayIndex}`} isCopyMode={isCopyMode} onClick={selectDay}>
            <ClickableArea height={height} width={width} x={0} y={top} />
            {isSelectedDay && (
                <CloseGroup
                    data-qa="schedule-close-day"
                    style={{
                        transform: `translate3d(${closeButtonLeft}px, ${top + 10}px, 0)`
                    }}
                    transform={`translate(${closeButtonLeft} ${top + 10})`}
                >
                    <CloseBorder
                        height={closeButtonHeight}
                        rx={closeButtonHeight / 2}
                        ry={closeButtonHeight / 2}
                        width={closeButtonWidth}
                    />
                    <TextContainer isHidden={!isSelectedDay}>
                        <CrossPath d={`M ${crossSize} ${closeButtonHeight * 0.7} l ${crossSize} ${-crossSize}`} />
                        <CrossPath d={`M ${2 * crossSize} ${closeButtonHeight * 0.7} l ${-crossSize} ${-crossSize}`} />
                        <CloseText
                            style={{
                                transform: `translate3d(${3 * crossSize}px, ${closeButtonHeight * 0.7}px, 0)`
                            }}
                            transform={`translate(${3 * crossSize} ${closeButtonHeight * 0.7})`}
                        >
                            {formatMessage(messages.close)}
                        </CloseText>
                    </TextContainer>
                </CloseGroup>
            )}
            <EventsContainer
                blur={
                    isCopyMode &&
                    copyDay &&
                    copyDay.get('dayToCopy') !== dayIndex &&
                    !copyDay.get('copyToList').includes(dayIndex)
                }
            >
                {eventsList}
            </EventsContainer>
            <Mask
                height={height}
                style={{ transform: `translate3d(0, ${top}px, 0)` }}
                transform={`translate(0 ${top})`}
                width={(isCopyMode ? 1.7 : 1) * sideMarginWidth}
            />
            <Mask
                height={height}
                style={{
                    transform: `translate3d(${width - sideMarginWidth}px, ${top}px, 0)`
                }}
                transform={`translate(${width - sideMarginWidth} ${top})`}
                width={sideMarginWidth}
            />
            <TextContainer
                style={{
                    transform: `translate3d(${sideMarginWidth / 2}px, ${top + height / 2}px, 0)`
                }}
                transform={`translate(${sideMarginWidth / 2} ${top + height / 2})`}
            >
                <DayLabel blurLabel={selectedDay !== null && !isSelectedDay} dy="4">
                    {moment.weekdays((dayIndex + 1) % 7)[0].toUpperCase()}
                </DayLabel>
            </TextContainer>
            <VelocityTransitionGroup
                component="g"
                enter={{
                    animation: { opacity: 1 },
                    duration: 200,
                    easing: EASING
                }}
            >
                {isAddMode && addEventButtons}
            </VelocityTransitionGroup>
            <CopyButtonContainer
                style={{
                    transform: `translate3d(${sideMarginWidth * 1.2}px, ${top + height / 2}px, 0)
						scale(${isCopyMode ? 1 : 0})`,
                    transitionDelay: isCopyMode ? `${0.2 + dayIndex * 0.02}s` : '0s'
                }}
                transform={`translate(${sideMarginWidth * 1.2} ${top + height / 2}) scale(${isCopyMode ? 1 : 0})`}
            >
                <CopyButton isCopiedTo={isCopiedTo} isSelectedToBeCopied={isSelectedToBeCopied} r="10" />
                <Line isCopiedTo={isCopiedTo} x1="-1" x2="-5" y1="3" y2="-1" />
                <Line isCopiedTo={isCopiedTo} x1="-1" x2="6" y1="3" y2="-4" />
            </CopyButtonContainer>
        </Container>
    );
}

export default injectIntl(Day);



// WEBPACK FOOTER //
// ./src/components/widgets/schedule/day/component.js